import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostVideoCommentsResult,
  PostVideoCommentsOptions,
  GetVideoCommentsOptions,
  GetVideoCommentsResult,
  getVideoComments,
  PostVideoCommentsFields,
  postVideoComments,
  GetVideoCommentsFields,
} from '../fetchers/video/comments';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoCommentsOptions<Fields extends GetVideoCommentsFields> =
  GetVideoCommentsOptions<Fields>;

/**
 * Get all the video comments on a video
 * This method returns every video comment on the specified video.
 */

export function useGetVideoComments<Fields extends GetVideoCommentsFields>(
  getOptions:
    | UseGetVideoCommentsOptions<Fields>
    | (() => UseGetVideoCommentsOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoCommentsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/videos/${options.where.videoId}/comments${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getVideoComments({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoComments, {
    endpoint: '/videos/:videoId/comments',
    method: 'GET',
  });
}

/**
 * Get all the video comments on a video
 * This method returns every video comment on the specified video.
 */

export function useGetVideoCommentsLazy<Fields extends GetVideoCommentsFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoCommentsResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoCommentsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/comments${serializeQuery(options)}`,
          getVideoComments({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoCommentsLazy, {
    endpoint: '/videos/:videoId/comments',
    method: 'GET',
  });
}

type UseGetVideoCommentsOptionsInfinite<Fields extends GetVideoCommentsFields> =
  GetVideoCommentsOptions<Fields>;

/**
 * Get all the video comments on a video
 * This method returns every video comment on the specified video.
 */

export function useGetVideoCommentsInfinite<Fields extends GetVideoCommentsFields>(
  getOptions:
    | UseGetVideoCommentsOptionsInfinite<Fields>
    | (() => UseGetVideoCommentsOptionsInfinite<Fields> | null),
  config?: SWRInfiniteConfiguration<GetVideoCommentsResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let getKey = (pageIndex: number, previousPageData: GetVideoCommentsResult<Fields> | null) => {
    if (options === null) {
      return null;
    }
    if (previousPageData && !previousPageData.paging.next) {
      return null;
    }
    let { perPage = 25, page: _page, ...rest } = options.query ?? {};
    let select = options.select.join(',');
    let queryString = Object.entries(rest ?? {})
      .filter(([, value]) => typeof value !== 'undefined')
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
    return [
      `/videos/${options.where.videoId}/comments?page=${
        pageIndex + 1
      }&perPage=${perPage}&fields=${select}&${queryString}`,
      pageIndex,
    ];
  };
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWRInfinite(
    getKey,
    options !== null
      ? ([_uri, pageIndex]: [string, number]) => {
          return getVideoComments({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            query: { ...options.query, page: pageIndex + 1 },
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoCommentsInfinite, {
    endpoint: '/videos/:videoId/comments',
    method: 'GET',
  });
}

type UsePostVideoCommentsOptions<Fields extends PostVideoCommentsFields> =
  PostVideoCommentsOptions<Fields>;

/**
 * Add a video comment to a video
 * This method adds a video comment to the specified video.
 */

export function usePostVideoComments<Fields extends PostVideoCommentsFields>() {
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PostVideoCommentsResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePostVideoCommentsOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await postVideoComments({
          ...options,
          baseUrl,
          headers: {
            ...options.headers,
            'Content-Type': 'application/json',
            Authorization: jwt ? `jwt ${jwt}` : '',
            'Vimeo-Page': `${xVimeoPage}`,
            'Accept-Language': locale ?? 'en',
          },
        });
        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostVideoComments, {
    endpoint: '/videos/:videoId/comments',
    method: 'POST',
  });
}
